import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {Object} ClassroomOverviewData
 * @property {number} classroom_id - The classroom ID
 * @property {string} classroom_name - The classroom name
 * @property {string} teacher_name - The teacher's name
 * @property {number} students_count - Number of students in the classroom
 * @property {number|null} classroom_limit - The classroom quiz limit, or null if not set
 * @property {boolean} extended - Whether the limit has been extended
 * @property {number} exhausted_students - Number of students who exhausted their attempts
 */

/**
 * @typedef {import("../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @description Custom hook to fetch classrooms quiz attempt limits overview
 */
export const useGetClassroomOverview = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [classrooms, setClassrooms] = useState(
        /** @type {ClassroomOverviewData[] | null | undefined} */(undefined),
    );
    const [meta, setMeta] = useState(/** @type {PaginationMeta | null} */(null));

    const { axiosInstance } = useAxios();

    /**
     * Fetch classrooms quiz attempt limits overview
     * @async
     * @param {Object} [params]
     * @param {number} [params.page] - Page number for pagination
     * @param {number} [params.per_page] - Number of items per page
     * @returns {Promise<void>}
     */
    const getClassroomOverview = async ({
        page = 1,
        per_page = 10,
    } = {}) => {
        try {
            setLoading(true);
            setError("");

            const query = new URLSearchParams();
            query.append("page", page);
            query.append("per_page", per_page);

            const url = `${TENANT_DOMAIN}/quiz/limit/classrooms/overview?${query.toString()}`;
            const response = await axiosInstance.get(url);

            setClassrooms(response?.data?.data || []);
            setMeta(response?.data?.meta || null);
        } catch (err) {
            setClassrooms(null);
            setMeta(null);

            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        classrooms,
        meta,
        loading,
        error,
        getClassroomOverview,
    };
};